/**
 * Les gestes de la page d'une offre ouverte par son lien, et ses deux écritures : l'accepter, la
 * masquer. Par `hash` (le lien d'un mail, sans compte requis côté serveur) la porte `…ByHash` ; par
 * id, la porte de la liste.
 *
 * Port de `GroupAttributionProposalScreen` et `SubstitutionProposalScreen` :
 *  - « Accepter la proposition » ouvre la confirmation (« Je candidate », « Je confirme que
 *    j'accepte ») ; « Masquer la proposition » part sans confirmation ;
 *  - la réponse reçue, la boîte « Succès » dit ce qui s'est passé, et « OK » revient d'où l'on vient
 *    (l'accueil pour un lien ouvert à froid) ; par id, la liste qui porte l'offre est relue ;
 *  - un refus se dit dans la confirmation (accepter), ou dans une boîte « Erreur » (masquer).
 * Un « Changement de créneau » se répond ICI, comme dans l'onglet : « Accepter » ouvre le choix de ses
 * horaires, qui partent avec l'acceptation (`live_session_ids`) ; « Je ne peux pas » se confirme en
 * disant ce qu'il déclenche. (L'ancienne page renvoyait le tuteur à l'onglet « Propositions ».)
 * Mesurés : `offer.accept` (ouvert avec la confirmation), `offer.hide` (ouvert avec « Je ne peux pas »
 * d'un changement de créneau).
 */
import { useReducer } from "react"

import { groupAttributionProposalsService } from "../../../../../../data/services/groupAttributionProposalsService"
import { substitutionProposalsService } from "../../../../../../data/services/substitutionProposalsService"
import { type TutorOfferLink } from "../../../../view-models/tutor/Availability/tutorOfferViewModel"
import { useBackOr } from "../../../hooks/useBackOr"
import { type UITutorOffer } from "../../../types/tutorDispoOffres"
import {
    slotChoiceFlowProps,
    toggleSlotChoice,
    type ToggleSlotAction,
    tutorOfferAcceptBody,
} from "./tutorOffersUiState"
import { type TutorOfferAnswer, useTutorOfferAnswer } from "./useTutorOfferAnswers"
import type { TutorOfferFlowProps } from "../../../../../../design-system/templates/TutorOfferFlow"

const SURFACE = "tutor_offer_link"

const BY_ID = {
    group: groupAttributionProposalsService.updateGroupAttributionProposalStatus,
    substitution: substitutionProposalsService.updateSubstitutionProposalStatus,
}
const BY_HASH = {
    group: groupAttributionProposalsService.updateGroupAttributionProposalStatusByHash,
    substitution: substitutionProposalsService.updateSubstitutionProposalStatusByHash,
}

/**
 * Les mots de la boîte « Succès », ceux des deux anciens écrans. Un changement de créneau accepté est
 * acquis tout de suite (le serveur retient le tuteur) : pas de « confirmation » à attendre.
 */
export function tutorOfferSuccessMessage(
    kind: TutorOfferLink["kind"],
    answer: TutorOfferAnswer,
    slotChange = false
): string {
    if (answer !== "accept") return "La proposition a été masquée."
    if (slotChange) return "C'est fait : tu gardes ton élève sur ses nouveaux horaires."
    return kind === "substitution"
        ? "La proposition a été acceptée !"
        : "La proposition a été acceptée avec succès ! Tu recevras une confirmation si le groupe est validé pour toi."
}

/** La feuille de la page : la confirmation (le choix des horaires d'un changement de créneau), ou « Je ne peux pas ». */
type PageFlowStep = "confirm" | "decline"

type PageUi = {
    /** L'étape ouverte, et celle par laquelle la feuille s'est ouverte : « Je ne peux pas » revient au choix. */
    flow: { step: PageFlowStep; openedAt: PageFlowStep } | null
    slotSelection: number[]
    sending: "accept" | "hide" | null
    flowError: string | null
    errorMessage: string | null
    successMessage: string | null
}

type PageUiAction =
    | { type: "OPEN_FLOW"; step: PageFlowStep; slotSelection?: number[] }
    | { type: "GO_STEP"; step: PageFlowStep }
    | ToggleSlotAction
    | { type: "CLOSE_FLOW" }
    | { type: "SENDING"; answer: "accept" | "hide" }
    | { type: "SENT"; message: string }
    | { type: "FAILED"; message: string }
    | { type: "DISMISS_ERROR" }

const INITIAL: PageUi = {
    flow: null,
    slotSelection: [],
    sending: null,
    flowError: null,
    errorMessage: null,
    successMessage: null,
}

function tutorOfferPageReducer(state: PageUi, action: PageUiAction): PageUi {
    switch (action.type) {
        case "OPEN_FLOW":
            return {
                ...state,
                flow: { step: action.step, openedAt: action.step },
                slotSelection: action.slotSelection ?? [],
                flowError: null,
            }
        case "GO_STEP":
            return state.flow == null
                ? state
                : { ...state, flow: { ...state.flow, step: action.step }, flowError: null }
        case "TOGGLE_SLOT":
            return {
                ...state,
                slotSelection: toggleSlotChoice(state.slotSelection, action.liveSessionId, action.pick),
                flowError: null,
            }
        case "CLOSE_FLOW":
            return { ...state, flow: null, slotSelection: [], flowError: null }
        case "SENDING":
            return { ...state, sending: action.answer, flowError: null, errorMessage: null }
        case "SENT":
            return { ...state, sending: null, flow: null, successMessage: action.message }
        case "FAILED":
            // Une réponse partie d'une feuille encore ouverte s'y refuse ; « Masquer » de la page n'en a pas.
            return state.flow != null
                ? { ...state, sending: null, flowError: action.message }
                : { ...state, sending: null, errorMessage: action.message }
        case "DISMISS_ERROR":
            return { ...state, errorMessage: null }
        default:
            return state
    }
}

export function useTutorOfferState(offer: UITutorOffer | null, link: TutorOfferLink, reloadList: () => void) {
    const [ui, dispatchUi] = useReducer(tutorOfferPageReducer, INITIAL)
    const back = useBackOr("menu")

    const callbacks = {
        onSuccess: (answer: TutorOfferAnswer) => {
            dispatchUi({
                type: "SENT",
                message: tutorOfferSuccessMessage(link.kind, answer, offer?.slotChange != null),
            })
            reloadList()
        },
        onError: (_answer: TutorOfferAnswer, message: string) => dispatchUi({ type: "FAILED", message }),
    }
    // Par lien de mail, la porte du hash (sans portée : une seule offre) ; sinon celle de la liste.
    const service = link.hash != null ? BY_HASH[link.kind] : BY_ID[link.kind]
    const scopeParam = link.hash != null ? undefined : "id"
    const answers = {
        accept: useTutorOfferAnswer("accept", service, SURFACE, scopeParam, callbacks),
        hide: useTutorOfferAnswer("hide", service, SURFACE, scopeParam, callbacks),
    }

    const send = (answer: "accept" | "hide", body: Record<string, unknown> = {}) => {
        if (ui.sending != null) return
        const sent =
            link.hash != null
                ? answers[answer].start({}, { ...body, hash: link.hash })
                : answers[answer].start({ id: link.id }, body)
        if (sent) dispatchUi({ type: "SENDING", answer })
    }

    const accept = () => {
        if (offer == null) return
        const body = tutorOfferAcceptBody(offer, ui.slotSelection)
        if (body != null) send("accept", body)
    }

    const openDecline = () => {
        answers.hide.opened()
        if (ui.flow == null) dispatchUi({ type: "OPEN_FLOW", step: "decline" })
        else dispatchUi({ type: "GO_STEP", step: "decline" })
    }

    const flow: TutorOfferFlowProps | null =
        offer != null && ui.flow != null
            ? {
                  visible: true,
                  onClose: () => dispatchUi({ type: "CLOSE_FLOW" }),
                  offer,
                  step: ui.flow.step,
                  entry: "page",
                  onConfirm: accept,
                  onHide: () => send("hide"),
                  // « Je ne peux pas » ouvert depuis le choix y revient ; ouvert depuis la page, il est seul.
                  onBack:
                      ui.flow.openedAt === "confirm"
                          ? () => dispatchUi({ type: "GO_STEP", step: "confirm" })
                          : undefined,
                  ...slotChoiceFlowProps(offer, ui.slotSelection, dispatchUi),
                  onDecline: openDecline,
                  sending: ui.sending,
                  errorMessage: ui.flowError ?? undefined,
              }
            : null

    return {
        onBack: back,
        onAccept: () => {
            answers.accept.opened()
            dispatchUi({ type: "OPEN_FLOW", step: "confirm", slotSelection: offer?.slotChange?.initialSelection })
        },
        // Un changement de créneau ne se masque pas d'un geste : « Je ne peux pas » dit d'abord ce qu'il déclenche.
        onHide: offer?.slotChange != null ? openDecline : () => send("hide"),
        sending: ui.sending,
        flow,
        successMessage: ui.successMessage,
        onDismissSuccess: back,
        errorMessage: ui.errorMessage,
        onDismissError: () => dispatchUi({ type: "DISMISS_ERROR" }),
    }
}
